import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Small, SmallBold } from '@comeon/ui';
import useInterval from '../../../../../hooks/use-interval';

import { ClockType, LiveGameStateDto, OvertimeClockBehaviour, PeriodType } from '../../api/sbapi/sbapi-api-model';

const ONE_SECOND_IN_MS = 1000;
const ONE_MINUTE_IN_SECONDS = 60;

export const convertSecondsToMinutesAndSeconds = (seconds: number): string => {
	const minutes = Math.floor(seconds / ONE_MINUTE_IN_SECONDS).toFixed(0);
	const parsedSeconds = seconds % ONE_MINUTE_IN_SECONDS;

	return `${'0'.concat(minutes).slice(-2)}:${'0'.concat(parsedSeconds.toString()).slice(-2)}`;
};

const getTimeDiff = (startDate: Date, endDate: Date): number => {
	return Math.round(Math.abs(endDate.getTime() - startDate.getTime()) / 1000);
};

const GameTimeDisplay = styled(SmallBold)`
	color: var(--main-text-supportive);
`;

const TimeDisplay = styled(Small)`
	display: inline-block;
	min-width: 35px; // Keep space for 000:00 format, so parent container will not jump after 99:59 -> 100:00.
	color: inherit;
`;

export function calculateIsGameInOvertime(
	clockRunning: boolean,
	clockType: ClockType,
	gameTime: number,
	periodEndTime: number
): boolean {
	if (clockRunning && clockType === ClockType.Stopwatch && periodEndTime && gameTime >= periodEndTime) {
		return true;
	}

	if (
		clockRunning &&
		clockType === ClockType.Timer &&
		((periodEndTime && gameTime < periodEndTime) || gameTime <= 0)
	) {
		return true;
	}

	return false;
}

export function calculateIsGameClockPaused(
	clockIsRunning: boolean,
	overtimeClockBehaviour: OvertimeClockBehaviour,
	isInOvertime: boolean
): boolean {
	return !clockIsRunning || (overtimeClockBehaviour === OvertimeClockBehaviour.StopCounting && isInOvertime);
}

export function calculateGameTimeSinceLastUpdate(gameTime: number, lastUpdatedOn: string, clockType: ClockType) {
	const timeDiff = getTimeDiff(new Date(), new Date(lastUpdatedOn));
	return clockType === ClockType.Stopwatch ? gameTime + timeDiff : gameTime - timeDiff;
}

const useGameTime = (
	{
		periodEndTime,
		clockRunning,
		overtimeClockBehaviour,
		gameTime: originalGameTime,
		clockType,
		periodType
	}: LiveGameStateDto,
	lastUpdatedOn: string
) => {
	const [gameTime, setGameTime] = useState(
		calculateGameTimeSinceLastUpdate(originalGameTime, lastUpdatedOn, clockType)
	);

	const isInOvertime = useMemo(() => {
		return calculateIsGameInOvertime(clockRunning, clockType, gameTime, periodEndTime);
	}, [clockRunning, clockType, gameTime, periodEndTime]);

	const isPaused = useMemo(() => {
		return calculateIsGameClockPaused(clockRunning, overtimeClockBehaviour, isInOvertime);
	}, [clockRunning, overtimeClockBehaviour, isInOvertime]);

	useInterval(
		() => {
			const secondSinceLastUpdate = calculateGameTimeSinceLastUpdate(originalGameTime, lastUpdatedOn, clockType);
			const nextSecond =
				clockType === ClockType.Stopwatch ? secondSinceLastUpdate + 1 : secondSinceLastUpdate - 1;

			setGameTime(nextSecond);
		},
		isPaused ? null : ONE_SECOND_IN_MS
	);

	useEffect(() => {
		setGameTime(calculateGameTimeSinceLastUpdate(originalGameTime, lastUpdatedOn, clockType));
	}, [clockType, lastUpdatedOn, originalGameTime]);

	const formattedGameTime = useMemo(() => {
		if (periodType === PeriodType.Break) {
			return convertSecondsToMinutesAndSeconds(originalGameTime);
		}

		if (clockType === ClockType.Timer && gameTime <= 0) {
			return '00:00';
		}

		if (isInOvertime && periodEndTime && overtimeClockBehaviour === OvertimeClockBehaviour.StopCounting) {
			return convertSecondsToMinutesAndSeconds(periodEndTime);
		}

		return convertSecondsToMinutesAndSeconds(gameTime);
	}, [clockType, isInOvertime, gameTime, originalGameTime, overtimeClockBehaviour, periodEndTime, periodType]);

	return { gameIsInOvertime: isInOvertime, clockIsPaused: isPaused, formattedGameTime };
};

type Props = {
	liveGameState: LiveGameStateDto;
	className?: string;
	lastUpdatedOn: string;
};

const SportsbookLiveGameState: FC<Props> = ({ liveGameState, className, lastUpdatedOn }) => {
	const { gameIsInOvertime, formattedGameTime } = useGameTime(liveGameState, lastUpdatedOn);

	if (!liveGameState.periodName) {
		return null;
	}

	return (
		<GameTimeDisplay className={className}>
			{liveGameState.periodName}{' '}
			{liveGameState.gameTime ? (
				<TimeDisplay className={className}>
					{formattedGameTime}
					{gameIsInOvertime && liveGameState.overtimeClockBehaviour === OvertimeClockBehaviour.StopCounting
						? '+'
						: null}
				</TimeDisplay>
			) : null}
		</GameTimeDisplay>
	);
};

export default SportsbookLiveGameState;
